import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import axiosInstance from "../axiosInstance";
import LOGO_BASE64 from "../assets/data/logoBase64";

const ReceiptViewer = () => {
  const { transactionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPrinted, setHasPrinted] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      try {
        const response = await axiosInstance.get(`/pdf/transaction/${transactionId}`);
        setData(response.data);
      } catch (err) {
        const errorMessage =
          err.response?.data?.message ||
          err.response?.statusText ||
          err.message ||
          "Failed to load transaction details";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (transactionId) {
      fetchTransactionDetails();
    }
  }, [transactionId]);

  useEffect(() => {
    if (!loading && !error && data && !hasPrinted) {
      const timer = setTimeout(() => {
        if (printRef.current) {
          window.focus();
          window.print();
          setHasPrinted(true);
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, error, data, hasPrinted]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box p={3}>
        <Alert severity="warning">No transaction data found</Alert>
      </Box>
    );
  }

  const { transaction, customerDetails, accountBalanceData, trayTypes, employerName, calculatedData } = data;
  const { sales = [], trays = [], paymentAmount = 0, dateOfTransaction } = transaction;
  const { salesTotal, totalCredits, transactionBalance, paymentReceived, trayPaymentTotal } = calculatedData;

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  };

  const formatCurrency = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getTrayTypeName = (trayTypeId) => {
    const tray = trayTypes.find(
      (t) => t.trayTypeId === trayTypeId || t.id === trayTypeId
    );
    return tray?.label || "Tray";
  };

  const getTrayTypePrice = (trayTypeId) => {
    const tray = trayTypes.find(
      (t) => t.trayTypeId === trayTypeId || t.id === trayTypeId
    );
    return tray?.price || 0;
  };

  const primaryColor = "#1976d2";
  const successColor = "#2e7d32";
  const errorColor = "#d32f2f";
  const accountBalance = ((customerDetails?.totalAmount || 0) - (customerDetails?.totalPaidAmount || 0)) || 0;

  return (
    <Box p={3} maxWidth="800px" mx="auto">
      <Paper ref={printRef} sx={{ p: 3 }}>
        <div className="receipt-container">
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 20, paddingBottom: 15, borderBottom: `2px solid ${primaryColor}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img
                src={LOGO_BASE64}
                alt="Company Logo"
                style={{ width: 50, height: 50, borderRadius: 4, objectFit: "cover" }}
              />
              <div>
                <div style={{ fontSize: 20, fontWeight: "bold", color: primaryColor }}>
                  {employerName || customerDetails?.employer || "Durga Bhavani Fruits"}
                </div>
                <div style={{ color: "#666", fontSize: 16 }}>
                  {customerDetails?.branchName || "Transaction Receipt"}
                </div>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: primaryColor, fontWeight: "medium", fontSize: 14 }}>
                <span style={{ color: "#ddd" }}>Date:</span> {formatDate(dateOfTransaction)}
              </div>
            </div>
          </div>

          {/* Customer Section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 20, fontWeight: "bold", color: primaryColor, marginBottom: 0, textTransform: "capitalize" }}>
              {customerDetails?.name || "N/A"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, color: "#666" }}>
              <span style={{ textTransform: "capitalize" }}>{customerDetails?.location || "N/A"}</span>
              {customerDetails?.mobile && (
                <span>Mobile: {customerDetails.mobile}</span>
              )}
            </div>
          </div>

          {/* Sales Section */}
          {sales.length > 0 && (
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 15 }}>
              <thead>
                <tr>
                  <th colSpan={5} style={{ background: primaryColor, color: "white", textAlign: "center", padding: 10, border: "1px solid #333" }}>
                    Sales
                  </th>
                </tr>
                <tr>
                  <th style={{ border: "1px solid #333", padding: 8, background: "#f5f5f5", width: 40 }}>#</th>
                  <th style={{ border: "1px solid #333", padding: 8, background: "#f5f5f5" }}>Item</th>
                  <th style={{ border: "1px solid #333", padding: 8, background: "#f5f5f5", textAlign: "right", width: 80 }}>Qty</th>
                  <th style={{ border: "1px solid #333", padding: 8, background: "#f5f5f5", textAlign: "right", width: 100 }}>Rate</th>
                  <th style={{ border: "1px solid #333", padding: 8, background: "#f5f5f5", textAlign: "right", width: 120 }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale, idx) => {
                  const qty = Number(sale.numberOfItems) || 0;
                  const rate = Number(sale.costPerItem) || 0;
                  const amount = qty * rate;
                  return (
                    <tr key={idx}>
                      <td style={{ border: "1px solid #333", padding: 8, textAlign: "center" }}>{idx + 1}</td>
                      <td style={{ border: "1px solid #333", padding: 8 }}>{sale.itemName}</td>
                      <td style={{ border: "1px solid #333", padding: 8, textAlign: "right" }}>{qty}</td>
                      <td style={{ border: "1px solid #333", padding: 8, textAlign: "right" }}>₹{formatCurrency(rate)}</td>
                      <td style={{ border: "1px solid #333", padding: 8, textAlign: "right" }}>₹{formatCurrency(amount)}</td>
                    </tr>
                  );
                })}
                <tr>
                  <td colSpan={4} style={{ border: "1px solid #333", padding: 8, fontWeight: "bold", background: "#f9f9f9", textAlign: "right" }}>
                    Total:
                  </td>
                  <td style={{ border: "1px solid #333", padding: 8, fontWeight: "bold", background: "#f9f9f9", textAlign: "right" }}>
                    ₹{formatCurrency(salesTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Payment and Tray Returns Section */}
          {(paymentReceived > 0 || trays.length > 0) && (
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 15 }}>
              <thead>
                <tr>
                  <th colSpan={4} style={{ background: successColor, color: "white", textAlign: "center", padding: 10, border: "1px solid #333" }}>
                    Payment and Tray Returns
                  </th>
                </tr>
              </thead>
              <tbody>
                {paymentReceived > 0 && (
                  <tr style={{ background: "#e8f5e9" }}>
                    <td colSpan={3} style={{ border: "1px solid #333", padding: 8, fontWeight: "bold" }}>
                      Cash/Online
                    </td>
                    <td style={{ border: "1px solid #333", padding: 8, textAlign: "right", color: successColor, fontWeight: "bold" }}>
                      ₹{formatCurrency(paymentReceived)}
                    </td>
                  </tr>
                )}
                {trays.map((tray, idx) => {
                  const qty = Number(tray.numberOfTrays) || 0;
                  const price = getTrayTypePrice(tray.trayTypeId);
                  const total = qty * price;
                  return (
                    <tr key={idx} style={{ background: "#e8f5e9" }}>
                      <td style={{ border: "1px solid #333", padding: 8, fontWeight: "bold" }}>
                        Tray Returned
                      </td>
                      <td style={{ border: "1px solid #333", padding: 8 }}>
                        {getTrayTypeName(tray.trayTypeId)}
                      </td>
                      <td style={{ border: "1px solid #333", padding: 8, textAlign: "center" }}>
                        {qty} × ₹{tray.excludePayment ? '0 (Payment Excluded)' : price}
                      </td>
                      <td style={{ border: "1px solid #333", padding: 8, textAlign: "right", color: successColor, fontWeight: "bold" }}>
                        ₹{tray.excludePayment ? 0 : formatCurrency(total)}
                      </td>
                    </tr>
                  );
                })}
                <tr style={{ background: "#c8e6c9", fontWeight: "bold" }}>
                  <td colSpan={3} style={{ border: "1px solid #333", padding: 8, fontWeight: "bold", textAlign: "right" }}>
                    Total Payment Received:
                  </td>
                  <td style={{ border: "1px solid #333", padding: 8, textAlign: "right", color: successColor, fontWeight: "bold", fontSize: 16 }}>
                    ₹{formatCurrency(totalCredits)}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          {/* Summary */}
          <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 15 }}>
            <tbody>
              <tr style={{ background: "#f5f5f5" }}>
                <td style={{ border: "1px solid #333", padding: 10, fontWeight: "bold" }}>
                  Sales Total
                </td>
                <td style={{ border: "1px solid #333", padding: 10, textAlign: "right", fontWeight: "bold" }}>
                  ₹{formatCurrency(salesTotal)}
                </td>
              </tr>
              {totalCredits > 0 && (
                <tr style={{ background: "#e8f5e9" }}>
                  <td style={{ border: "1px solid #333", padding: 10, fontWeight: "bold" }}>
                    Total Credits
                  </td>
                  <td style={{ border: "1px solid #333", padding: 10, textAlign: "right", fontWeight: "bold", color: successColor }}>
                    - ₹{formatCurrency(totalCredits)}
                  </td>
                </tr>
              )}
              <tr style={{ background: "#e3f2fd" }}>
                <td style={{ border: "1px solid #333", padding: 12, fontWeight: "bold", fontSize: 16 }}>
                  {transactionBalance >= 0 ? "Balance Due" : "Credit Balance"}
                </td>
                <td style={{ border: "1px solid #333", padding: 12, textAlign: "right", fontWeight: "bold", fontSize: 18, color: transactionBalance >= 0 ? errorColor : successColor }}>
                  ₹{formatCurrency(Math.abs(transactionBalance))}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Account Balance */}
          <div style={{ textAlign: "center", marginTop: 20, paddingTop: 15, borderTop: "2px dashed #ccc", fontSize: 18 }}>
            <span style={{ fontWeight: "bold" }}>
              Overall Account Balance:
            </span>
            <span style={{ fontWeight: "bold", marginLeft: 10, fontSize: 16, color: accountBalance > 0 ? errorColor : successColor }}>
              ₹{formatCurrency(Math.abs(accountBalance))} {accountBalance > 0 ? "(Due)" : "(Credit)"}
            </span>
          </div>
        </div>
      </Paper>
    </Box>
  );
};

export default ReceiptViewer;